import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";

import { UpsertFaxProfileRequest } from "hooks/useFaxProfile";
import { useCelleraTheme } from "theme/useCelleraTheme";
import {
  upsertFaxProfileSchema,
  UpsertFaxProfileFormData,
} from "validations/faxProfileSchemas";

interface FaxProfileFormDialogProps {
  open: boolean;
  mode: "create" | "edit";
  initialSinchServiceId?: string;
  initialFaxNumber?: string;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (data: UpsertFaxProfileRequest) => void;
}

const FaxProfileFormDialog: React.FC<FaxProfileFormDialogProps> = ({
  open,
  mode,
  initialSinchServiceId,
  initialFaxNumber,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const { colors } = useCelleraTheme();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<UpsertFaxProfileFormData>({
    resolver: yupResolver(upsertFaxProfileSchema),
    mode: "onChange",
    defaultValues: {
      sinchServiceId: initialSinchServiceId ?? "",
      faxNumber: initialFaxNumber ?? "",
    },
  });

  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      reset({
        sinchServiceId: initialSinchServiceId ?? "",
        faxNumber: initialFaxNumber ?? "",
      });
    }
    prevOpenRef.current = open;
  }, [open, initialFaxNumber, initialSinchServiceId, reset]);

  const title =
    mode === "create" ? "Configure Fax Profile" : "Edit Fax Profile";

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
      aria-labelledby="fax-profile-dialog-title"
      slotProps={{ paper: { sx: { borderRadius: 3, margin: "16px" } } }}
    >
      <DialogTitle id="fax-profile-dialog-title" sx={{ pb: 1 }}>
        <Typography
          variant="h5"
          sx={{ color: colors.text.primary, fontWeight: 600 }}
        >
          {title}
        </Typography>
      </DialogTitle>

      <Divider />

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ display: "flex", flexDirection: "column" }}
      >
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          <Controller
            name="sinchServiceId"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Sinch Service ID"
                placeholder="01KPAVJBJC0FAHCFQ7WT3ER914"
                variant="outlined"
                error={!!errors.sinchServiceId}
                helperText={
                  errors.sinchServiceId?.message ??
                  "Find this in Sinch Dashboard → Fax → Services."
                }
                slotProps={{ htmlInput: { maxLength: 128 } }}
                disabled={loading}
                fullWidth
              />
            )}
          />

          <Controller
            name="faxNumber"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Fax Number"
                placeholder="+15612600684"
                variant="outlined"
                error={!!errors.faxNumber}
                helperText={
                  errors.faxNumber?.message ??
                  "Must be E.164 format. Example: +15612600684."
                }
                disabled={loading}
                fullWidth
              />
            )}
          />

          <Typography variant="caption" sx={{ color: colors.text.secondary }}>
            Cellera will validate this binding with Sinch and ensure HIPAA flags
            are off.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ gap: 2, p: "16px", justifyContent: "flex-end" }}>
          <Button
            onClick={onClose}
            disabled={loading}
            variant="outlined"
            sx={{
              borderRadius: "80px",
              width: "150px",
              fontWeight: 500,
              borderColor: colors.border.main,
              color: colors.text.primary,
              "&:hover": {
                borderColor: colors.border.dark,
                backgroundColor: colors.background.secondary,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isValid || loading}
            sx={{
              borderRadius: "80px",
              width: "150px",
              fontWeight: 500,
              minWidth: 96,
            }}
          >
            {loading ? (
              <CircularProgress
                size={18}
                sx={{ color: colors.primary.contrast }}
              />
            ) : (
              "Save"
            )}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default FaxProfileFormDialog;
